import React, { useCallback, useEffect, useRef, useState } from "react";
import { browserHistory, Link } from "react-router";

import Attendees from "./details/attendees/TripAttendees";
import Loader from "../common/Loader";
import Metrics from "./details/metrics/TripMetrics";
import ProgramBreadcrumbsHeader from "../common/ProgramBreadcrumbsHeader";
import Students from "./details/students/TripStudents";
import Shluchim from "./details/shluchim/TripShluchim";
import TripEventCard from "./details/TripEventCard";
import { Unauthorized } from "../../lib/coc-common-components";

import TripEventsApi from "../../services/resources/TripEventsApi";
import SystemApi from "../../services/resources/SystemApi";
import AuthService, { PermissionClaims } from "../../services/AuthService";
import { ApiCallErrorMessageHandler } from "../../lib/coc-common-scripts";
import axios from "axios";

const getTripTabs = (hasCheckin) => [
  { name: "Students", id: "students", component: Students },
  { name: "Shluchim", id: "shluchim", component: Shluchim },
  { name: "Metrics", id: "metrics", component: Metrics },
  {
    name: `Attendees${hasCheckin ? " & Check-in" : ""}`,
    id: "attendees",
    component: Attendees,
  },
];

export default function TripEventPage(props) {
  const {
    mobileMode,
    location: {
      pathname,
      query: { tab: queryTab },
      search,
    },
    params: { scheduleId },
  } = props;

  const [authorized, setAuthorized] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [systemLists, setSystemLists] = useState({
    tripRegistrationStatuses: [],
  });
  const [tripEvent, setTripEvent] = useState(null);
  const [tripEventsList, setTripEventsList] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  const isCheckInEnabled = tripEvent?.isCheckInEnabled;
  const [tabs, setTabs] = useState(getTripTabs(isCheckInEnabled));

  const didMountRef = useRef(false);

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  useEffect(() => {
    if (!AuthService.UserHasClaim(PermissionClaims.TripDataView)) {
      setAuthorized(false);
    }
  }, []);

  useEffect(() => {
    setTabs(getTripTabs(isCheckInEnabled));
  }, [isCheckInEnabled]);

  useEffect(
    () => {
      (async function () {
        const updatedTripEventsList = await getTripEventsList(scheduleId);

        getTripDetails(scheduleId, updatedTripEventsList, (newPathname) => {
          const currentTabIndex = tabs
            .map((tab) => tab.id)
            .indexOf(decodeURIComponent(queryTab));
          toTab(currentTabIndex >= 0 ? currentTabIndex : 0, newPathname);
        });

        getSystemLists();
      })();
    },
    // only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // this effect is currently needed only to support switching between trips from the "Current Programs" sidebar
  useEffect(
    () => {
      (async function () {
        // ensure this effect does not run when the onMount effect runs
        if (!didMountRef.current) {
          didMountRef.current = true;
          return;
        }

        const hasTripForSchedule = tripEventsList.some(
          (p) => p.programScheduleID.toString() === scheduleId,
        );
        const updatedTripEventsList = hasTripForSchedule
          ? tripEventsList
          : await getTripEventsList(scheduleId);

        getTripDetails(scheduleId, updatedTripEventsList);
      })();
    },
    // only run if scheduleId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleId],
  );

  const getTripEventsList = async (scheduleIdToLoad) => {
    try {
      setErrorMessage("");
      setLoading(true);

      const _tripEventsList = await TripEventsApi.getTripEvents(
        apiSignalRef.current.token,
        scheduleIdToLoad,
      );
      if (_tripEventsList.length) {
        setTripEventsList(_tripEventsList);
      }

      return _tripEventsList;
    } catch (err) {
      if (!axios.isCancel(err)) {
        setErrorMessage(ApiCallErrorMessageHandler(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const getTripDetails = async (
    scheduleIdToLoad,
    _tripEventsList = tripEventsList,
    onFetchDetails = null,
  ) => {
    try {
      setErrorMessage("");
      setLoading(true);

      const eventScheduleId =
        scheduleIdToLoad ||
        (_tripEventsList.length && _tripEventsList[0].programScheduleID);
      const tripEvent = await TripEventsApi.getTripEvent(
        apiSignalRef.current.token,
        eventScheduleId,
      );

      // the order of these two setters is significant until we upgrade to React 18+ which has state batching
      setTripEvent(tripEvent);
      setLoading(false);

      const pathnameUpdate =
        eventScheduleId !== scheduleId ? `/trips/${eventScheduleId}` : "";

      if (onFetchDetails) {
        onFetchDetails(pathnameUpdate);
      } else if (pathnameUpdate) {
        browserHistory.replace(`${pathnameUpdate}${search || ""}`);
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        const is404 = (err.response && err.response.status) === 404;

        setErrorMessage(
          is404
            ? "404"
            : ApiCallErrorMessageHandler(
                err,
                err.message ||
                  "Sorry, something went wrong and we could not retrieve trip details. Please try again.",
              ),
        );
        setLoading(false);
      }
    }
  };

  const refreshTripEvent = useCallback(async () => {
    try {
      const updatedTripEvent = await TripEventsApi.getTripEvent(
        apiSignalRef.current.token,
        scheduleId,
      );
      setTripEvent(updatedTripEvent);
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error("Failed to refresh trip event", err);
      }
    }
  }, [scheduleId]);

  const getSystemLists = async () => {
    try {
      const systemLists = await SystemApi.lists([
        "genders",
        "tripAttendeeStatuses",
        "tripRegistrationStatuses",
        "tripRegistrationWorkflowSteps",
        "yearOverYearChartIntervals",
      ]);
      setSystemLists(systemLists);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setErrorMessage(ApiCallErrorMessageHandler(err));
      }
    }
  };

  const toTab = (index, newPathname) => {
    if (queryTab !== index) {
      setTabIndex(index);
      browserHistory.replace(
        `${newPathname || pathname}?tab=${tabs[index].id}`,
      );
    }
  };

  if (!authorized) {
    return <Unauthorized userName={AuthService.getCurrentUser().name} />;
  }

  const hasSettingsAccess = AuthService.UserHasClaim(
    PermissionClaims.TripFullEdit,
  );

  return (
    <div className="trip-page page">
      <ProgramBreadcrumbsHeader scheduleId={scheduleId} />
      {!tripEvent && loading ? (
        <div className="full-page-loader">
          <Loader />
        </div>
      ) : errorMessage ? (
        errorMessage === "404" ? (
          <div className="text-center">
            <p>No event has been configured for this program schedule.</p>
            <Link
              className="btn btn-info mt-16"
              to={`/programs/trips/${scheduleId}`}
            >
              Configure Advanced Settings
            </Link>
          </div>
        ) : (
          <div className="full-page-error-text error-text">
            <img src="/img/error.svg" alt="error robot" height="240" />
            <p>{errorMessage}</p>
          </div>
        )
      ) : (
        !!tripEvent && (
          <>
            <TripEventCard
              hasSettingsAccess={hasSettingsAccess}
              loading={loading}
              onChangeTripEvent={(_, scheduleId) => getTripDetails(scheduleId)}
              tripEvent={tripEvent}
              tripEventsList={tripEventsList}
            />

            {!loading && (
              <>
                <div
                  className="trip-details-tabs flex"
                  style={{ marginLeft: "2px" }}
                >
                  {tabs.map((tab, index) => (
                    <p
                      key={index}
                      className={`large-text ${index > 0 ? "ml-40" : ""} ${
                        tabIndex === index ? "active" : ""
                      }`}
                      onClick={() => toTab(index)}
                    >
                      {tab.name}
                    </p>
                  ))}
                </div>

                <div className="card trip-card">
                  {React.createElement(tabs[tabIndex].component, {
                    mobileMode,
                    refreshTripEvent,
                    tripEvent,
                    systemLists,
                  })}
                </div>
              </>
            )}
          </>
        )
      )}
    </div>
  );
}
